import { useState, useEffect } from "react"
import { Home, ArrowLeft, Compass, FileX } from 'lucide-react'


const Button = ({
  children,
  className = "",
  variant = "default",
  size = "default",
  disabled = false,
  onClick,
  ...props
}) => {
  const baseClasses =
    "inline-flex items-center justify-center rounded-md font-medium transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"

  const variants = {
    default: "bg-gray-900 text-white hover:bg-gray-800 active:bg-gray-700",
    destructive: "bg-red-600 text-white hover:bg-red-700 active:bg-red-800",
    outline: "border border-gray-300 bg-white hover:bg-gray-50 active:bg-gray-100 text-gray-900",
    secondary: "bg-gray-100 text-gray-900 hover:bg-gray-200 active:bg-gray-300",
    ghost: "hover:bg-gray-100 active:bg-gray-200 text-gray-900",
    link: "text-blue-600 underline-offset-4 hover:underline",
    gradient: "bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white shadow-lg hover:shadow-xl",
  }

  const sizes = {
    default: "h-10 px-4 py-2 text-sm",
    sm: "h-9 rounded-md px-3 text-sm",
    lg: "h-11 rounded-md px-8 text-base",
    xl: "h-12 rounded-lg px-10 text-lg",
    icon: "h-10 w-10",
  }

  return (
    <button
      className={`${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`}
      disabled={disabled}
      onClick={onClick}
      {...props}
    >
      {children}
    </button>
  )
}

const Input = ({ className = "", ...props }) => (
  <input
    className={`flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
    {...props}
  />
)

const Card = ({ children, className = "", onClick }) => (
  <div
    className={`bg-white rounded-lg shadow-sm border border-gray-200 transition-all duration-200 hover:shadow-md ${className} ${onClick ? "cursor-pointer" : ""}`}
    onClick={onClick}
  >
    {children}
  </div>
)

const CardContent = ({ children, className = "" }) => <div className={`p-4 ${className}`}>{children}</div>

export default function NotFoundPage() {
  // Animated numbers for 404
  const [animatedNumbers, setAnimatedNumbers] = useState({ first: 0, second: 0, third: 0 })

  useEffect(() => {
    // Animate the 404 numbers on load
    const animateNumbers = () => {
      let frame = 0
      const animate = () => {
        frame++
        setAnimatedNumbers({
          first: Math.min(frame * 2, 4),
          second: Math.min(Math.max(frame - 20, 0) * 2, 0),
          third: Math.min(Math.max(frame - 40, 0) * 2, 4),
        })
        if (frame < 60) {
          requestAnimationFrame(animate)
        }
      }
      requestAnimationFrame(animate)
    }

    const timer = setTimeout(animateNumbers, 500)
    return () => clearTimeout(timer)
  }, [])

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-gray-50 flex items-center justify-center px-4 py-8">
      <div className="max-w-2xl w-full text-center">
        
        {/* Animated 404 */}
        <div className="flex items-center justify-center gap-2 sm:gap-4 mb-8">
          <div className="text-6xl sm:text-8xl lg:text-9xl font-bold bg-gradient-to-r from-teal-500 via-emerald-500 to-teal-600 bg-clip-text text-transparent animate-bounce" style={{animationDelay: '0s'}}>
            {animatedNumbers.first}
          </div>
          <div className="text-6xl sm:text-8xl lg:text-9xl font-bold bg-gradient-to-r from-red-500 via-orange-500 to-red-600 bg-clip-text text-transparent animate-bounce" style={{animationDelay: '0.2s'}}>
            {animatedNumbers.second}
          </div>
          <div className="text-6xl sm:text-8xl lg:text-9xl font-bold bg-gradient-to-r from-teal-500 via-emerald-500 to-teal-600 bg-clip-text text-transparent animate-bounce" style={{animationDelay: '0.4s'}}>
            {animatedNumbers.third}
          </div>
        </div>

        {/* Error Illustration */}
        <div className="w-32 h-32 sm:w-40 sm:h-40 mx-auto mb-8 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center shadow-lg">
          <div className="relative">
            <FileX className="w-16 h-16 sm:w-20 sm:h-20 text-gray-400" />
            <div className="absolute -top-2 -right-2 w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-r from-teal-500 to-emerald-600 rounded-full flex items-center justify-center">
              <Compass className="w-3 h-3 sm:w-4 sm:h-4 text-white animate-spin" style={{animationDuration: '3s'}} />
            </div>
          </div>
        </div>

        {/* Error Message */}
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Page Not Found
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 mb-6 max-w-lg mx-auto">
            The page you're looking for doesn't exist or has been moved. 
            Let's get you back on track!
          </p>
        </div>

        {/* Primary Actions */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            variant="gradient"
            size="xl"
            onClick={() => window.location.href = '/'}
            className="flex-1 sm:flex-none"
          >
            <Home className="w-5 h-5 mr-2" />
            Go to Homepage
          </Button>
          <Button
            variant="outline"
            size="xl"
            onClick={() => window.history.back()}
            className="flex-1 sm:flex-none"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Go Back
          </Button>
        </div>

        {/* Help Text */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <p className="text-sm text-gray-500">
            Still having trouble? 
            <button 
              onClick={() => window.location.href = '/contact'}
              className="text-teal-600 hover:text-teal-700 underline ml-1"
            >
              Contact our support team
            </button>
          </p>
        </div>
      </div>

      {/* Custom Styles */}
      <style jsx>{`
        .bg-clip-text {
          -webkit-background-clip: text;
          background-clip: text;
        }
        @keyframes bounce {
          0%, 20%, 53%, 80%, 100% {
            transform: translate3d(0, 0, 0);
          }
          40%, 43% {
            transform: translate3d(0, -10px, 0);
          }
          70% {
            transform: translate3d(0, -5px, 0);
          }
          90% {
            transform: translate3d(0, -2px, 0);
          }
        }
        .animate-bounce {
          animation: bounce 2s ease-in-out infinite;
        }
        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }
        .animate-spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  )
}